import { useEffect, useState } from "react";
import { LocalInitClientDTO, LocalInitClientProductDTO } from "../models/local/local.response";
import { MenuFilter } from "../utils/clientMenu";
import { getProductTagTypeFromString } from "../models/base/ProductTagType";


export const useProductFilter = (
    localData: LocalInitClientDTO | undefined,
    selectedCategoryId: string
) => {

    const [sortOption, setSortOption] = useState<string>("");

    const [filters, setFilters] = useState<MenuFilter>({
        searchText: "",
        selectedTags: [],
        minPrice: "",
        maxPrice: "",
        onlyNew: false,
        onlyWithImage: false,
        allItems: false,
    });

    const [displayProducts, setDisplayProducts] = useState<LocalInitClientProductDTO[]>([]);
    const [displaySubcategories, setDisplaySubcategories] = useState<{id: string, name: string, description: string | null, products: LocalInitClientProductDTO[]}[]>([]);

    useEffect(() => {
        if (!localData) return;

        let allFilteredProducts: LocalInitClientProductDTO[] = [];
        let filteredSubcategories: {id: string, name: string, description: string | null, products: LocalInitClientProductDTO[]}[] = [];

        if (filters.allItems) {
            let products = localData.categories.flatMap(c =>
                c.subcategories.flatMap(sc => sc.products)
            );
            products = applyFilters(products);
            products = applySort(products);
            allFilteredProducts = products;
        } else {
            const category = localData.categories.find(c => c.id === selectedCategoryId);
            if (category) {
                category.subcategories.forEach(sc => {
                    let scProducts = applyFilters(sc.products);
                    scProducts = applySort(scProducts);
                    if (scProducts.length > 0) {
                        filteredSubcategories.push({
                            id: sc.id,
                            name: sc.name,
                            description: sc.description,
                            products: scProducts
                        });
                        allFilteredProducts = allFilteredProducts.concat(scProducts);
                    }
                });
            }
        }

        setDisplayProducts(allFilteredProducts);
        setDisplaySubcategories(filteredSubcategories);

    }, [localData, selectedCategoryId, filters, sortOption]);

    const minPrice = (product: LocalInitClientProductDTO): number => {
        if (!product.productVariants || product.productVariants.length === 0) {
            return 0;
        }
        return Math.min(...product.productVariants.map(v => v.price));
    }

    const applyFilters = (products: LocalInitClientProductDTO[]): LocalInitClientProductDTO[] => {
        return products.filter(product => {

            if (filters.searchText) {
                const search = filters.searchText.toLowerCase();
                const nameMatch = product.name.toLowerCase().includes(search);
                const descriptionMatch = product.description.toLowerCase().includes(search);

                if (!nameMatch && !descriptionMatch) {
                    return false;
                }
            }

            if (filters.selectedTags.length > 0) {
                const hasAllTags = filters.selectedTags.every(tag => {
                    const tagType = getProductTagTypeFromString(tag);
                    return tagType !== null && product.tags.includes(tagType);
                });

                if (!hasAllTags) {
                    return false;
                }
            }

            if (filters.minPrice) {
                const min = parseFloat(filters.minPrice);
                if (isNaN(min) || minPrice(product) < min) {
                    return false;
                }
            }

            if (filters.maxPrice) {
                const max = parseFloat(filters.maxPrice);
                if (isNaN(max) || minPrice(product) > max) {
                    return false;
                }
            }

            if (filters.onlyNew && !product.isNew) {
                return false;
            }

            if (filters.onlyWithImage && !product.imageUrl) {
                return false;
            }

            return true;
        });
    };

    const applySort = (products: LocalInitClientProductDTO[]): LocalInitClientProductDTO[] => {

        const sorted = [...products];

        if (sortOption === "price-low") {
            sorted.sort((a, b) => minPrice(a) - minPrice(b));
        } else if (sortOption === "price-high") {
            sorted.sort((a, b) => minPrice(b) - minPrice(a));
        } else if (sortOption === "newest") {
            sorted.sort((a, b) => {
                if (a.isNew === b.isNew) return 0;
                return a.isNew ? -1 : 1;
            });
        }

        return sorted;
    };

    return {
        filters,
        setFilters,
        sortOption,
        setSortOption,
        displayProducts,
        displaySubcategories,
        productCount: displayProducts.length,
    };
};